import { useState, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { DocEntry, docsGroups } from '../config/docs.registry';
import { cn } from '@/utils/cn';

interface DocsSidebarProps {
  docs: DocEntry[];
  onNavigate?: () => void;
}

export function DocsSidebar({ docs, onNavigate }: DocsSidebarProps) {
  const [search, setSearch] = useState('');

  const filteredDocs = useMemo(() => {
    if (!search.trim()) return docs;
    const lowerSearch = search.toLowerCase();
    return docs.filter(doc => doc.title.toLowerCase().includes(lowerSearch));
  }, [docs, search]);

  const groupedDocs = useMemo(() => {
    const groups: Record<string, DocEntry[]> = {};
    docsGroups.forEach(group => {
      groups[group] = filteredDocs.filter(doc => doc.group === group).sort((a, b) => a.sortOrder - b.sortOrder);
    });
    return groups;
  }, [filteredDocs]);

  return (
    <div className="flex h-full flex-col bg-white border-r border-slate-200">
      <div className="p-4 border-b border-slate-100 shrink-0">
        <h2 className="text-sm font-semibold text-slate-800 mb-3">Tài liệu hướng dẫn</h2>
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Tìm bài viết..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-shadow"
          />
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-3 space-y-6">
        {docsGroups.map((group) => {
          const groupDocs = groupedDocs[group];
          if (!groupDocs || groupDocs.length === 0) return null;

          return (
            <div key={group} className="space-y-1">
              <h3 className="px-3 mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                {group}
              </h3>
              <div className="space-y-0.5">
                {groupDocs.map(doc => (
                  <NavLink
                    key={doc.id}
                    to={`/docs/${doc.id}`}
                    onClick={onNavigate}
                    className={({ isActive }) => cn(
                      "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                      isActive 
                        ? "bg-emerald-50 text-emerald-700" 
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    )}
                  >
                    <i className={cn(doc.icon, "text-lg opacity-70")} />
                    {doc.title}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}

        {filteredDocs.length === 0 && (
          <div className="text-center py-8 px-4 text-sm text-slate-500">
            Không tìm thấy bài viết nào phù hợp.
          </div>
        )}
      </div>
    </div>
  );
}
